import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import styled from "styled-components";
import TPC_URL from "../../../utils/TPC_URL";
import { useParams } from "react-router-dom";
import {
  Typography,
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  FormControlLabel,
  Select,
  FormControl,
  InputLabel,
  Button,
  TextField,
  Stack,
  Switch,
  Snackbar,
  Alert,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { AG_GRID_LOCALE_KR } from "@ag-grid-community/locale";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: var(--mui-palette-background-default);
  overflow: hidden;
  box-sizing: border-box;
`;

const FrameContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow: hidden;
  background-color: var(--mui-palette-background-paper);
  box-shadow: var(--mui-shadows-2);
`;

const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid var(--mui-palette-divider);
`;

const ToolbarLeft = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #f5f5f5;
  border-radius: 4px;
  padding: 0 8px;
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InnerContainer = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
`;

const LoadingContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-top: 1px solid var(--mui-palette-divider);
`;

const gridStyles = `
  .ag-theme-material .ag-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    border-right: 1px solid #e0e0e0;
  }
  .ag-theme-material .ag-header-cell {
    border-right: 1px solid #e0e0e0;
    background: #faf7f7;
  }
  .ag-theme-material .ag-header-cell-label {
    justify-content: center;
  }
  .changed-row {
    background-color: #ffe0e0;
  }
  .changed-row:hover {
    background-color: #fcd7d7 !important;
  }
  .completed-row {
    background-color: #feffd4;
  }
  .completed-row:hover {
    background-color: #f6f7c3 !important;
  }
  .ag-header-cell-text {
    font-size: 1rem;
  }
  .ag-theme-material .ag-root-wrapper {
    border: none !important;
  }
  .ag-theme-material {
    --ag-borders: none;
    --ag-row-border-width: 1px;
    --ag-row-border-color: #e0e0e0;
  }
  .ag-header-cell-resize {
    background-color: #e0e0e0;
    width: 4px;
  }
  .ag-theme-material .ag-paging-panel {
    display: none;
  }
  .ag-body-viewport::-webkit-scrollbar,
  .ag-center-cols-viewport::-webkit-scrollbar,
  .ag-center-cols-container::-webkit-scrollbar,
  .ag-body-horizontal-scroll-viewport::-webkit-scrollbar,
  .ag-body-vertical-scroll-viewport::-webkit-scrollbar,
  .ag-virtual-list-viewport::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  .ag-body-viewport::-webkit-scrollbar-track,
  .ag-center-cols-viewport::-webkit-scrollbar-track,
  .ag-center-cols-container::-webkit-scrollbar-track,
  .ag-body-horizontal-scroll-viewport::-webkit-scrollbar-track,
  .ag-body-vertical-scroll-viewport::-webkit-scrollbar-track,
  .ag-virtual-list-viewport::-webkit-scrollbar-track {
    background: transparent;
  }
  .ag-body-viewport::-webkit-scrollbar-thumb,
  .ag-center-cols-viewport::-webkit-scrollbar-thumb,
  .ag-center-cols-container::-webkit-scrollbar-thumb,
  .ag-body-horizontal-scroll-viewport::-webkit-scrollbar-thumb,
  .ag-body-vertical-scroll-viewport::-webkit-scrollbar-thumb,
  .ag-virtual-list-viewport::-webkit-scrollbar-thumb {
    background-color: rgba(99, 96, 150, 0.7);
    border-radius: 5px;
  }
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background-color: rgba(99, 96, 150, 0.7);
    border-radius: 5px;
  }
`;
function Processed() {
  const { group_code, project_code } = useParams();
  const [rowData, setRowData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [yearOptions, setYearOptions] = useState([]);
  const [monthOptions, setMonthOptions] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [filterActive, setFilterActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [showCompleted, setShowCompleted] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const columnDefs = useMemo(
    () => [
      {
        headerName: "주문 번호",
        field: "order_number",
        sortable: false,
        headerClass: "no-sort",
        width: 120,
        flex: 0,
      },
      {
        headerName: "납기일",
        field: "local_due_date",
        width: 120,
        flex: 0,
      },
      {
        headerName: "Material Grouping",
        field: "material_grouping",
        width: 180,
        flex: 0,
      },
      {
        headerName: "Add.descr.4",
        field: "add_descr_4",
        headerClass: "no-sort",
        width: 150,
        flex: 0,
      },
      {
        headerName: "생산 지시 수량",
        field: "production_quantity",
        type: "numericColumn",
        valueFormatter: (params) =>
          params.value ? params.value.toLocaleString() : "",
        width: 130,
        flex: 0,
      },
      {
        headerName: "변경 내역",
        field: "change_text",
        flex: 1,
        cellRenderer: (params) => {
          const CustomCellRenderer = () => {
            if (!params.value) return null;
            const parts = params.value.split(",");
            if (parts.length === 1) return <div>{params.value}</div>;
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  lineHeight: "1.2",
                }}
              >
                <div>{parts[0].trim()}</div>
                <div>{parts.slice(1).join(",").trim()}</div>
              </div>
            );
          };
          return <CustomCellRenderer />;
        },
      },
      {
        headerName: "삭스 생산일정",
        field: "earliest_start_date",
        width: 130,
        flex: 0,
      },
      {
        headerName: "등록일",
        field: "register_date",
        width: 120,
        flex: 0,
      },
      {
        headerName: "주문 수량",
        field: "order_item_quantity",
        type: "numericColumn",
        valueFormatter: (params) =>
          params.value ? params.value.toLocaleString() : "",
        width: 110,
        flex: 0,
      },
      {
        headerName: "Header Text",
        field: "header_text",
        width: 120,
        flex: 0,
      },
    ],
    []
  );
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
      suppressMenu: false,
      minWidth: 100,
      cellStyle: { textAlign: "center" },
    }),
    []
  );
  const getRowClass = useCallback((params) => {
    console.log("params.data.status : ", params.data.status);
    if (params.data && params.data.status === 1) {
      return "completed-row";
    }
    if (params.data && params.data.is_change === 1) {
      return "changed-row";
    }
    return "";
  }, []);
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = showCompleted
        ? `/api/tpc/getProcessedDataEntire?group_code=${group_code}&project_code=${project_code}`
        : `/api/tpc/getProcessedData?group_code=${group_code}&project_code=${project_code}`;
      const response = await axios.get(TPC_URL + endpoint);
      if (response.data && Array.isArray(response.data)) {
        setRowData(response.data);
        setFilteredData(response.data);
        const dateMap = new Map();
        response.data.forEach((item) => {
          if (item.local_due_date) {
            const [year, month] = item.local_due_date.split("-");
            if (year && month) {
              if (!dateMap.has(year)) {
                dateMap.set(year, new Set());
              }
              dateMap.get(year).add(month);
            }
          }
        });
        const years = Array.from(dateMap.keys()).sort((a, b) => b - a);
        setYearOptions(years);
        setSelectedYear("");
        setSelectedMonth("");
        setSearchTerm("");
        setFilterActive(false);
        setCurrentPage(1);
      } else {
        setRowData([]);
        setFilteredData([]);
        setError("데이터 형식이 유효하지 않습니다");
      }
    } catch (err) {
      console.error("데이터 로딩 오류:", err);
      setError(`데이터를 불러오는 중 오류가 발생했습니다: ${err.message}`);
      setRowData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [group_code, project_code, showCompleted]);
  useEffect(() => {
    if (selectedYear && rowData.length > 0) {
      const months = new Set();
      rowData.forEach((item) => {
        if (item.local_due_date) {
          const [year, month] = item.local_due_date.split("-");
          if (year === selectedYear && month) {
            months.add(month);
          }
        }
      });
      setMonthOptions(Array.from(months).sort());
    } else {
      setMonthOptions([]);
    }
    setSelectedMonth("");
  }, [selectedYear, rowData]);
  const applyFilter = useCallback(() => {
    if (!selectedYear && !selectedMonth && !searchTerm) {
      setFilteredData(rowData);
      setFilterActive(false);
      setSnackbar({
        open: true,
        message: "모든 필터가 초기화되었습니다",
        severity: "info",
      });
      return;
    }
    let filtered = [...rowData];
    if (selectedYear) {
      filtered = filtered.filter((item) => {
        if (!item.local_due_date) return false;
        const [year, month] = item.local_due_date.split("-");
        if (selectedMonth) {
          return year === selectedYear && month === selectedMonth;
        }
        return year === selectedYear;
      });
    }
    if (searchTerm) {
      const normalizedSearchTerm = searchTerm.replace(/\s+/g, "").toLowerCase();
      filtered = filtered.filter((item) => {
        const orderNumberMatch =
          item.order_number &&
          item.order_number.toLowerCase().includes(normalizedSearchTerm);
        const materialGroupingMatch =
          item.material_grouping &&
          item.material_grouping
            .replace(/\s+/g, "")
            .toLowerCase()
            .includes(normalizedSearchTerm);
        const addDescr4Match =
          item.add_descr_4 &&
          item.add_descr_4
            .replace(/\s+/g, "")
            .toLowerCase()
            .includes(normalizedSearchTerm);
        return orderNumberMatch || materialGroupingMatch || addDescr4Match;
      });
    }
    setFilteredData(filtered);
    setFilterActive(true);
    setCurrentPage(1);
    setSnackbar({
      open: true,
      message: "필터가 적용되었습니다",
      severity: "success",
    });
  }, [selectedYear, selectedMonth, searchTerm, rowData]);
  const clearFilter = useCallback(() => {
    setSelectedYear("");
    setSelectedMonth("");
    setSearchTerm("");
    setShowCompleted(false);
    setFilteredData(rowData);
    setFilterActive(false);
    setCurrentPage(1);
    setSnackbar({
      open: true,
      message: "모든 필터가 초기화되었습니다",
      severity: "info",
    });
    fetchData();
  }, [rowData, fetchData]);
  useEffect(() => {
    if (filteredData.length > 0) {
      setTotalPages(Math.ceil(filteredData.length / pageSize));
    } else {
      setTotalPages(0);
    }
  }, [filteredData, pageSize]);
  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize]);
  const handleCompletedToggle = useCallback(() => {
    if (!showCompleted) {
      setSnackbar({
        open: true,
        message: "완료 주문이 포함된 전체 주문을 표시합니다.",
        severity: "success",
      });
    } else {
      setSnackbar({
        open: true,
        message: "미완료된 주문만을 표시합니다.",
        severity: "success",
      });
    }
    setShowCompleted(!showCompleted);
  }, [showCompleted]);
  useEffect(() => {
    fetchData();
  }, [fetchData, showCompleted]);
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };
  return (
    <Wrapper>
      <style>{gridStyles}</style>
      <FrameContainer>
        <ToolbarContainer>
          <ToolbarLeft>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ fontSize: "22px !important", color: "#111" }}
            >
              업로드 결과
            </Typography>
          </ToolbarLeft>
          <ToolbarRight>
            <FormControlLabel
              control={
                <Switch
                  checked={showCompleted}
                  onChange={handleCompletedToggle}
                  color="primary"
                />
              }
              label={
                showCompleted ? "전체 주문 보기 중" : "미완료 주문만 보는중"
              }
              sx={{ height: 40, my: 0 }}
            />
            <FormControl variant="outlined" size="small" sx={{ height: 40 }}>
              <TextField
                placeholder="검색어를 입력하세요."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon
                      sx={{ color: "#777", mr: 1 }}
                      fontSize="small"
                    />
                  ),
                  endAdornment: searchTerm && (
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm("")}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
                sx={{ width: 200, height: 40 }}
              />
            </FormControl>
            <FilterContainer>
              <FormControl size="small" sx={{ minWidth: 100, height: 40 }}>
                <InputLabel id="year-select-label">연도</InputLabel>
                <Select
                  labelId="year-select-label"
                  value={selectedYear}
                  label="연도"
                  onChange={(e) => setSelectedYear(e.target.value)}
                  sx={{ height: 40 }}
                >
                  <MenuItem value="">
                    <em>전체</em>
                  </MenuItem>
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 80, height: 40 }}>
                <InputLabel id="month-select-label">월</InputLabel>
                <Select
                  labelId="month-select-label"
                  value={selectedMonth}
                  label="월"
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  disabled={!selectedYear}
                  sx={{ height: 40 }}
                >
                  <MenuItem value="">
                    <em>전체</em>
                  </MenuItem>
                  {monthOptions.map((month) => (
                    <MenuItem key={month} value={month}>
                      {month}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="primary"
                onClick={applyFilter}
                sx={{ height: 40, minWidth: 60 }}
              >
                적용
              </Button>
              {filterActive && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={clearFilter}
                  sx={{ height: 40, minWidth: 60 }}
                >
                  초기화
                </Button>
              )}
            </FilterContainer>
          </ToolbarRight>
        </ToolbarContainer>
        <InnerContainer>
          {loading ? (
            <LoadingContainer>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                데이터를 불러오는 중...
              </Typography>
            </LoadingContainer>
          ) : error ? (
            <LoadingContainer>
              <Typography variant="h6" color="error">
                {error}
              </Typography>
            </LoadingContainer>
          ) : (
            <div
              className="ag-theme-material"
              style={{ height: "100%", width: "100%" }}
            >
              <AgGridReact
                rowData={currentPageData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                pagination={false}
                rowSelection="multiple"
                localeText={AG_GRID_LOCALE_KR}
                animateRows={true}
                enableCellTextSelection={true}
                suppressRowClickSelection={true}
                rowHeight={48}
                headerHeight={56}
                getRowClass={getRowClass}
                suppressFloatingFilter={true}
                overlayNoRowsTemplate="표시할 데이터가 없습니다"
                overlayLoadingTemplate="<span class='ag-overlay-loading-center'>데이터를 불러오는 중...</span>"
              />
            </div>
          )}
        </InnerContainer>
        <PaginationContainer>
          <div>
            <Typography variant="body2" color="text.secondary">
              {filteredData.length > 0
                ? `총 ${filteredData.length}건 중 ${
                    (currentPage - 1) * pageSize + 1
                  }-${Math.min(
                    currentPage * pageSize,
                    filteredData.length
                  )}건 표시`
                : "표시할 데이터가 없습니다"}
            </Typography>
          </div>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 100, height: 40 }}>
              <InputLabel id="page-size-select-label">페이지 크기</InputLabel>
              <Select
                labelId="page-size-select-label"
                value={pageSize}
                label="페이지 크기"
                onChange={(e) => {
                  setPageSize(e.target.value);
                  setCurrentPage(1);
                }}
                sx={{ height: 40 }}
              >
                {[50, 100, 150, 200].map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
                sx={{ height: 40, minWidth: 60 }}
              >
                처음
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                sx={{ height: 40, minWidth: 60 }}
              >
                이전
              </Button>
              <Typography sx={{ alignSelf: "center", mx: 1 }}>
                {`${currentPage} / ${totalPages || 1}`}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                sx={{ height: 40, minWidth: 60 }}
              >
                다음
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage(totalPages)}
                sx={{ height: 40, minWidth: 60 }}
              >
                마지막
              </Button>
            </Stack>
          </Stack>
        </PaginationContainer>
      </FrameContainer>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Wrapper>
  );
}

export default Processed;
